import { groupBy, uniq } from 'lodash';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Checkbox,
  InputSearch,
  LoadingSkeleton,
  Selector,
  type SelectorOptionPropsInterface,
  TableListV2,
  useQueryFetch
} from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { getIASCategories } from '@/resources/contextualTargeting.resources';
import { CustomIASCell } from './CustomIASCell';

import './iasCategoriesSelector.scss';

type CategoryOption = {
  id: string;
  name: string;
  targeting: 'avoidance' | 'positive';
};

type State = {
  categoryTypeFilter?: string[];
  targetingTypeFilter?: string[];
  textFilter?: string;
};

export function IASCategoriesSelector() {
  const { formatMessage } = useIntl();
  const {
    setProp,
    setUnknownIncluded,
    targeting: { contextualTargeting, unknownIncluded },
    user,
    userRoles
  } = useTargetingContext();

  const isAvoidance = Boolean(contextualTargeting?.iasCategories?.avoid);

  const [{ categoryTypeFilter, targetingTypeFilter, textFilter }, setState] = useState<State>({
    categoryTypeFilter: [],
    targetingTypeFilter: [],
    textFilter: ''
  });

  const iasCategoriesToken = {};
  const { data: iasCategories = [], isLoading } = useQueryFetch(['iasCategories'], async () =>
    getIASCategories(iasCategoriesToken)
  );
  const orderedIasCategoriesDictionary = useMemo(
    () => structuredClone(iasCategories || []).sort((a, b) => a.category.localeCompare(b.category)),
    [iasCategories]
  );

  const categoryGroups = useMemo(
    () =>
      Object.entries(groupBy(orderedIasCategoriesDictionary, 'category')).map(([categoryId, values]) => ({
        items: values.map(({ id }) => id),
        label: categoryId
      })),
    [orderedIasCategoriesDictionary]
  );

  const categoryData = useMemo(
    () =>
      orderedIasCategoriesDictionary.map(({ id, name, targeting }) => ({
        id,
        name,
        targeting
      })),
    [orderedIasCategoriesDictionary]
  );

  if (isLoading) {
    return (
      <div className="categoryListContent">
        <LoadingSkeleton
          className="categoryListContent__loading"
          count={1}
          delay={0}
          duration={3}
          height="60px"
          width="100%"
        />
      </div>
    );
  }

  const handleChangeSelection = (items: CategoryOption[]) => {
    setProp('contextualTargeting', {
      ...contextualTargeting,
      iasCategories: {
        ...contextualTargeting?.iasCategories,
        categories: items.map(({ id }) => id),
        enabled: Boolean(items.length)
      }
    });
  };

  const handleAllowAvoidChange = (isAvoid: boolean) => {
    setProp('contextualTargeting', {
      ...contextualTargeting,
      iasCategories: {
        ...contextualTargeting?.iasCategories,
        avoid: isAvoid
      }
    });
  };

  const handleTargetingFilter = (options: SelectorOptionPropsInterface[]) => {
    setState(prev => ({
      ...prev,
      targetingTypeFilter: options.map(({ id }) => id as string)
    }));
  };

  const handleCategoryFilter = (options: SelectorOptionPropsInterface[]) => {
    setState(prev => ({
      ...prev,
      categoryTypeFilter: options.map(({ id }) => id as string)
    }));
  };

  const handleTextFilter = (value: string) => {
    setState(prev => ({
      ...prev,
      textFilter: value
    }));
  };

  const columns = [
    {
      Cell: ({ ...cell }) => <CustomIASCell cell={cell} />,
      Header: 'Name',
      accessor: 'lineName',
      className: 'iasCategories__tableListListItem',
      customCell: true,
      textAlign: 'left'
    }
  ];

  const typeOptions = uniq(iasCategories.map(el => el.targeting));
  const targetingTypeOptions = typeOptions.map(option => ({
    id: option,
    label: formatMessage({
      defaultMessage: option,
      id: `targeting.contentCategories.ias.filters.type.${option}`
    })
  }));

  const categoryTypeOptions = uniq(iasCategories.sort((a, b) => a.category.localeCompare(b.category))).map(
    ({ id: optionId, name: optionLabel }) => ({
      id: optionId,
      label: optionLabel
    })
  );

  const defaultSelectedRows = contextualTargeting?.iasCategories?.categories?.reduce(
    (acc, categoryId) => ({
      ...acc,
      [categoryId]: true
    }),
    {}
  );

  const showUnknownIncluded =
    Object.keys(defaultSelectedRows).length > 0 && checkUserResourcePermissions('unknownIncluded', user, userRoles);

  return (
    <div className={`categoryListContent${isAvoidance ? ' categoryListContent--exclude' : ''}`}>
      <div className="categoryListContent__filters">
        <Selector
          className="listFilter__typeSelector"
          onChange={handleTargetingFilter}
          options={targetingTypeOptions}
          placeholder={formatMessage({
            defaultMessage: 'Targeting type',
            id: 'targeting.contentCategories.ias.filters.type.placeholder'
          })}
          tiny
          type="multiple"
        />
        <Selector
          className="listFilter__categorySelector"
          onChange={handleCategoryFilter}
          options={categoryTypeOptions}
          placeholder={formatMessage({
            defaultMessage: 'Category type',
            id: 'targeting.contentCategories.ias.filters.category.placeholder'
          })}
          tiny
          type="multiple"
        />
        <InputSearch
          id="ias-text-filter"
          lens
          name="ias-text-filter"
          onChange={(value: string) => handleTextFilter(value)}
          placeholder={formatMessage({
            defaultMessage: 'Search by name',
            id: 'targeting.contentCategories.ias.filters.name.placeholder'
          })}
          tiny
        />
      </div>
      <div className="categoryListContent__options">
        <div>
          {showUnknownIncluded && (
            <Checkbox
              classes="categoryListContent__optionsSwitch"
              defaultActive={unknownIncluded.thirdPartyContentCategories}
              id="targetUnknown-IAS-switch"
              labelText={formatMessage({
                defaultMessage: 'Target unknown',
                id: 'targeting.contentCategories.targetUnknown'
              })}
              onClickAction={(_, value) => setUnknownIncluded('thirdPartyContentCategories', value)}
              tiny
              type="switch"
            />
          )}
        </div>
        <Checkbox
          classes="categoryListContent__optionsSwitch"
          defaultActive={isAvoidance}
          id="avoid-IAS-switch"
          labelText={formatMessage({
            defaultMessage: 'Select categories to be excluded',
            id: 'targeting.contentCategories.ias.avoidanceLabel'
          })}
          onClickAction={(_, checked) => handleAllowAvoidChange(checked)}
          tiny
          type="switch"
        />
      </div>
      <TableListV2
        className="categoryListContent__table"
        columns={columns}
        config={{
          groupsConfig: {
            groups: categoryGroups,
            includeUngrouped: true,
            selectableGroups: true
          },
          maxHeight: 450,
          numberOfColumns: 4,
          onChangeSelection: handleChangeSelection,
          pagination: {
            manual: true,
            show: false
          },
          searchEmptyState: {
            description: formatMessage({
              defaultMessage: 'Please change your search terms and try again',
              id: 'targeting.contentCategories.ias.filters.name.emptyState.description'
            }),
            fontSizes: {
              description: '12px',
              icon: '18px',
              title: '14px'
            },
            icon: 'fal fa-empty-set',
            show: true,
            spacing: 'small',
            title: formatMessage({
              defaultMessage: 'Your search did not match any results',
              id: 'targeting.contentCategories.ias.filters.name.emptyState.title'
            })
          },
          selectableItem: true,
          toolbar: {
            showSelectAll: true
          },
          type: 'list'
        }}
        data={categoryData}
        defaultSelectedRows={defaultSelectedRows}
        filters={{
          id: categoryTypeFilter,
          targeting: targetingTypeFilter,
          text: textFilter
        }}
        isSelectableList
      />
    </div>
  );
}
